import { useTranslation } from 'next-i18next/pages';
import React from 'react';

import { useMediaQuery } from '@/hooks/shared/useMediaQuery';

import Area from '@/components/apps/layouts/shared/Area';
import SectionTitle from '@/components/apps/ui/title/SectionTitle';
import { AniSlideUp } from '@/components/shared/animations/basic';
import BlurImage from '@/components/shared/ui/BlurImage';

import theme from '@/styles/theme';

import { useIndustrialUseCaseGroups } from './useIndustrialPageData';

/**
 * ### SolutionMachineKeySection
 *
 * Renders the three production use cases Listen AI Industrial is grouped
 * into: inspection on the line, inspection at the end of the line, and
 * monitoring of the line's own equipment. Predictive maintenance is explained
 * here as one of the three rather than given a page of its own.
 *
 * Every figure shown comes from the use-case copy as authored; this component
 * never composes or rounds one.
 *
 * @returns The rendered use-case section element
 * @see {@link useIndustrialUseCaseGroups} The copy rendered here
 */
export default function SolutionMachineKeySection() {
  const { t } = useTranslation('common');
  const isDesktop = useMediaQuery({
    query: `(min-width: ${theme`screens.lg`})`,
  });

  const groups = useIndustrialUseCaseGroups(t);

  return (
    <section
      className="bg-apps-brandGrayBlack pt-[clamp(100px,10.417vw,200px)]"
      data-analytics-section="solution_types"
    >
      <Area>
        <SectionTitle title={t('3대 양산 유즈케이스')} color="lighter" />
        <ul className="lg:grid lg:grid-cols-3 lg:gap-10">
          {groups.map((group, index) => (
            <AniSlideUp key={group.title} delay={0.2 * index} element="li">
              <div className="mt-10">
                <div className="relative flex aspect-335/160 items-center justify-center overflow-hidden rounded-[20px] shadow-[0px_0px_30px_0px_rgba(0,0,0,0.12)] after:pointer-events-none after:absolute after:inset-0 after:z-[1] after:bg-[linear-gradient(180deg,rgba(0,0,0,0.28)_0%,rgba(0,0,0,0.55)_55%,rgba(0,0,0,0.72)_100%)] after:content-[''] lg:aspect-[467/200]">
                  <BlurImage
                    src={isDesktop ? group.image.pc : group.image.mobile}
                    alt=""
                    fill
                    sizes="(min-width: 1480px) 467px, (min-width: 1024px) calc((100vw - 120px) / 3), calc(100vw - 40px)"
                    style={{ objectFit: 'cover' }}
                  />
                  {/*
                    z-[5] 는 no-op 이 아니다. 부모가 display:flex 라 이 h3 는 flex item 이고,
                    flexbox 명세상 flex item 은 position:static 이어도 z-index 가 적용돼
                    쌓임 맥락을 만든다. 이 값이 없으면 fill(=position:absolute) 로 깔리는
                    BlurImage 와 하단 비네팅 after 가상 요소(z-[1]) 가 제목 위를 덮으므로
                    반드시 유지해야 한다.
                  */}
                  <h3 className="z-[5] break-keep px-[16px] text-center text-[clamp(19px,5.2vw,26px)] font-bold leading-[140%] tracking-[-0.02em] text-balance text-apps-coreGrayWhite lg:text-[clamp(22px,2.15vw,32px)] lg:leading-[130%]">
                    {group.title}
                  </h3>
                </div>
                {/* 원본에 base font-size 가 없어 body 값을 상속한다. lg 에서만 20px 로 커진다. */}
                <p className="mt-[16px] font-medium leading-[140%] tracking-[-0.32px] text-apps-brandGray900 lg:mt-[20px] lg:text-[20px] lg:leading-[160%] lg:tracking-[-0.4px]">
                  {group.description}
                </p>
                <ul className="mt-[8px] inline-flex flex-wrap gap-[8px] font-medium text-apps-brandGray600 lg:mt-[16px] lg:text-[18px] lg:leading-[160%] lg:tracking-[-0.36px]">
                  {group.tags.map((tag) => (
                    <li key={tag}>#{tag}</li>
                  ))}
                </ul>
              </div>
            </AniSlideUp>
          ))}
        </ul>
      </Area>
    </section>
  );
}
